import { collection, query, where, orderBy, getDocs } from 'firebase/firestore'
import { db } from '../firebase'

export class SmartCardService {
  constructor(customerName) {
    this.customerName = customerName
    this.collectionName = `${customerName}_cc_txns`
  }

  async getTransactions(startDate, endDate) {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      console.log(`[SmartCard] Fetching transactions from ${this.collectionName} for dates ${startDate} to ${endDate}`)

      const txnRef = collection(db, this.collectionName)
      const q = query(
        txnRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      )

      const snapshot = await getDocs(q)
      const transactions = []
      const devices = new Set()

      snapshot.forEach((doc) => {
        const data = doc.data()
        
        // Only include processed transactions
        if (data.status !== 'processed') {
          return
        }
        
        devices.add(data.deviceId)

        if (data.txnType === 2 && Array.isArray(data.txns)) {
          data.txns.forEach((txnString, index) => {
            const parsed = this.parseModernTransaction(txnString, data, doc.id, index)
            if (parsed) {
              transactions.push(parsed)
            }
          })
        } else {
          const parsed = this.parseLegacyTransaction(data, doc.id)
          if (parsed) {
            transactions.push(parsed)
          }
        }
      })

      transactions.sort((a, b) => b.datetime - a.datetime)

      console.log(`[SmartCard] Loaded ${transactions.length} transactions from ${snapshot.size} documents`)

      return {
        transactions,
        devices: Array.from(devices).sort()
      }
    } catch (error) {
      console.error('[SmartCard] Error fetching transactions:', error)
      throw error
    }
  }

  parseModernTransaction(txnString, docData, docId, index) {
    try {
      const parts = txnString.split(',')
      if (parts.length < 3) return null

      const [txnTimeNs, priceStr, selection, ...productNameParts] = parts
      const productName = productNameParts.join(',').trim()
      const txnTimeMs = parseInt(txnTimeNs) / 1000000

      return {
        id: `${docId}_${index}`,
        docId,
        txnId: txnTimeNs,
        timestamp: Math.floor(txnTimeMs / 1000),
        datetime: new Date(txnTimeMs),
        deviceId: docData.deviceId,
        location: docData.location || '',
        employee: docData['Employee/UserIdentity'] || docData.employeeIdentity || '',
        cardHolderName: docData.cardHolderName || '',
        displayCardNumber: docData.displayCardNumber || '',
        cardId: docData.cardId || '',
        selection: selection,
        productName: productName,
        amount: parseFloat(priceStr) || 0,
        creditedAmount: docData.creditedAmount || 0,
        maxDaily: docData.MaxDaily || 0,
        status: docData.status || 'processed',
        currency: docData.currency || 'INR',
        make: docData.make || '',
        posId: docData.posId || '',
        txnType: 'modern',
        date: this.parseDate(docData.date)
      }
    } catch (error) {
      console.error('[SmartCard] Error parsing modern transaction:', error, txnString)
      return null
    }
  }

  parseLegacyTransaction(docData, docId) {
    try {
      const txnTimeMs = docData.time ? docData.time * 1000 : Date.now()
      const make = docData.make || ''
      const rawSelection = docData.Selection || ''
      const convertedSelection = this.convertLegacySelection(rawSelection, make)

      return {
        id: docId,
        docId,
        txnId: docData.txnId || docId,
        timestamp: docData.time || Math.floor(Date.now() / 1000),
        datetime: new Date(txnTimeMs),
        deviceId: docData.deviceId,
        location: docData.location || '',
        employee: docData['Employee/UserIdentity'] || docData.employeeIdentity || '',
        cardHolderName: docData.cardHolderName || '',
        displayCardNumber: docData.displayCardNumber || '',
        cardId: docData.cardId || '',
        selection: convertedSelection,
        productName: docData.SelectionName || '',
        amount: docData.txnAmount || 0,
        creditedAmount: docData.creditedAmount || 0,
        maxDaily: docData.MaxDaily || 0,
        status: docData.status || 'processed',
        currency: docData.currency || 'INR',
        make: make,
        posId: docData.posId || '',
        txnType: 'legacy',
        date: this.parseDate(docData.date)
      }
    } catch (error) {
      console.error('[SmartCard] Error parsing legacy transaction:', error, docData)
      return null
    }
  }

  convertLegacySelection(selection, make) {
    if (!selection) return ''
    
    const selectionStr = String(selection).trim()
    if (!selectionStr) return ''
    
    const makeLower = (make || '').trim().toLowerCase()
    
    if (makeLower === 'seaga') {
      const numericSelection = parseInt(selectionStr, 10)
      if (!isNaN(numericSelection)) {
        return String(numericSelection + 101)
      }
    } else if (makeLower === 'vendstop') {
      let hexValue = selectionStr.toLowerCase()
      if (hexValue.startsWith('0x')) {
        hexValue = hexValue.slice(2)
      }
      const decimalValue = parseInt(hexValue, 16)
      if (!isNaN(decimalValue)) {
        return String(decimalValue)
      }
    }
    
    return selectionStr
  }

  parseDate(dateInt) {
    if (!dateInt) return new Date()
    const dateStr = dateInt.toString()
    const year = dateStr.substring(0, 4)
    const month = dateStr.substring(4, 6)
    const day = dateStr.substring(6, 8)
    return new Date(`${year}-${month}-${day}`)
  }

  static formatDateTime(datetime) {
    return datetime.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  static formatCurrency(amount, currency = 'INR') {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount)
  }
}

export function createSmartCardService(userClaims) {
  if (!userClaims || !userClaims.customer) {
    throw new Error('Valid user claims with customer name required')
  }
  return new SmartCardService(userClaims.customer)
}
